import { PLP_FILTER_ORDER } from 'helpers/constants/PlpFilters';
import qs from 'qs';
import { FacetFilter, Sort, SortOrderType } from 'shared/types/query/ProductQuery';
import { Params, SearchParams } from 'types/next';

export enum PageFolderType {
  CATEGORY = 'frontastic/category',
  PDP = 'frontastic/product-detail-page',
}

export class RouteHelpers {
  static encodeToURL = (queryObj: object, addQueryPrefix: boolean = true) => {
    return qs.stringify(queryObj, {
      addQueryPrefix: addQueryPrefix,
      arrayFormat: 'comma',
      encodeValuesOnly: true,
      format: 'RFC1738',
    });
  };

  static decodeToObj = (queryString: string) => {
    return qs.parse(queryString, {
      ignoreQueryPrefix: true,
      comma: true,
    });
  };

  static slugify = (value: string) => {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^\w]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  static generatePlpURL = (appliedFacets: FacetFilter[], sort: Sort) => {
    const currentParams = RouteHelpers.decodeToObj(window.location.search);
    const searchQuery = currentParams.q ? `${RouteHelpers.encodeToURL({ q: currentParams.q })}` : '';

    if (appliedFacets?.length) {
      const urlObject = appliedFacets?.reduce(
        (acc, facet) => {
          const identifier = facet.urlKey;
          if (identifier && facet.facetData.terms) {
            // Sort the terms of the filters alphabetically
            acc[identifier] = facet.facetData.terms.sort((a, b) => a.localeCompare(b));
          }
          return acc;
        },
        {} as Record<string, string[]>,
      );

      // Sort filters by order defined by SOL
      const sortedUrlObject = Object.fromEntries(
        Object.entries(urlObject).sort(([keyA], [keyB]) => {
          const indexA = PLP_FILTER_ORDER.indexOf(keyA);
          const indexB = PLP_FILTER_ORDER.indexOf(keyB);
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return 0;
        }),
      );
      const queryString = RouteHelpers.encodeToURL(sortedUrlObject, searchQuery ? false : true);
      return (
        window.location.pathname +
        searchQuery +
        (searchQuery && queryString ? '&' : '') +
        queryString +
        getSortQueryString(sort, !!searchQuery || !!queryString)
      );
    } else {
      return window.location.pathname + searchQuery + getSortQueryString(sort, !!searchQuery);
    }
  };
}

const getSortQueryString = (sort: Sort, hasExistingParams: boolean) => {
  if (!sort || sort.orderType === SortOrderType.FEATURED) {
    return '';
  }

  const prefix = hasExistingParams ? '&' : '?';
  const sortValue = Object.keys(SortOrderType)
    .find((key) => SortOrderType[key as keyof typeof SortOrderType] === sort.orderType)
    ?.toLowerCase();

  return `${prefix}sort=${sortValue}`;
};

export const getSelectedFacetCount = (searchParams: SearchParams, excludedParams: string[] = ['q', 'sort', 'page', 'limit']): number => {
  const activeFacets = Object.keys(searchParams).filter((key) => !excludedParams.includes(key));
  
  return activeFacets
    .flatMap((key) => (searchParams[key] || '').split(','))
    .map((v) => v.trim())
    .filter(Boolean).length;
};

export const generateCanonicalUrl = (pageFolderType: string, params: Params, searchParams: SearchParams) => {
  let canonicalUrl = 'https://www.shadesoflight.com';

  if (params?.slug?.length > 0) {
    canonicalUrl = canonicalUrl + '/' + params.slug.join('/');
  }


  if (pageFolderType === PageFolderType.CATEGORY) {

    if (getSelectedFacetCount(searchParams) > 1) {
      return canonicalUrl;
    }

    //Take the first query param and only the first value if it's a comma-separated list
    if (Object.keys(searchParams).length > 0) {
      let queryObj: Record<string, string> = {};
      const firstEntry = Object.entries(searchParams)[0];
      if (firstEntry) {
        const [key, value] = firstEntry;
        // Split by comma and take only the first value
        const firstValue = value.split(',')[0];
        queryObj[key] = firstValue;
      }
      const queryParams = RouteHelpers.encodeToURL(queryObj);
      canonicalUrl = canonicalUrl + queryParams;
    }
  }

  return canonicalUrl;
};
